/* eslint-disable @typescript-eslint/no-shadow */
import React, { useRef, useState } from 'react';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { colorPalette } from '@sharefiledev/antd-config';
import { App, Button, Flex, Modal, theme, Tooltip, Typography } from 'antd';
import { useHistory } from 'react-router-dom';
import { useAppStore } from '../../../common/store';
import { sendDocumentPendoTrackingEvent } from '../../../common/tracking/documentGeneratorEvent';
import {
	SendSignatureRequestFooterAnalyticsIds,
	SignatureRequestCreatedFromDocumentTemplateTrackEvent,
} from '../../../common/tracking/events';
import {
	DocumentMergeAction,
	SIGNATURE_REQUEST_EXPIRATION,
	SignatureRequestSteps,
	SignatureRequestType,
} from '../../../constants';
import { DocgenClient } from '../../../data/docgenClient';
import { handleError, handleErrorWithServerMessage } from '../../../data/errorHandler';
import { PatchPayload, PatchPayloadItem } from '../../../data/interfaces/APIRequest';
import {
	signatureRequestCreatePath,
	signatureRequestDashboardPath,
	templateDetailsPath,
} from '../../../routes';
import { t } from '../../../utils';
import { hasSystemFilledFields } from '../../../utils/document';
import { emailValidator } from '../../../utils/validator';

const { Text } = Typography;

const getAnalyticsId = currentStep => {
	switch (currentStep) {
		case SignatureRequestSteps.ReviewAndSend:
			return SendSignatureRequestFooterAnalyticsIds.SendSignatureRequest;
		case SignatureRequestSteps.PopulateFields:
			return SendSignatureRequestFooterAnalyticsIds.NextStepSelectRecipients;
		case SignatureRequestSteps.SelectRecipients:
			return SendSignatureRequestFooterAnalyticsIds.NextStepReviewAndSend;
	}
};

const getButtonText = (currentStep, t) => {
	let buttonText;
	if (currentStep === SignatureRequestSteps.PopulateFields) {
		buttonText = t('SendSignatureRequest.footer.generateDocument');
	} else if (currentStep === SignatureRequestSteps.ReviewAndSend) {
		buttonText = t('SendSignatureRequest.footer.sendRequest');
	} else {
		buttonText = t('SendSignatureRequest.footer.reviewAndSend');
	}
	return buttonText;
};

const getDataTestIdForNextButton = currentStep => {
	let dataTestId;
	if (currentStep === SignatureRequestSteps.PopulateFields) {
		dataTestId = 'docgen-generate-document';
	} else if (currentStep === SignatureRequestSteps.ReviewAndSend) {
		dataTestId = 'docgen-send-for-signature';
	} else {
		dataTestId = 'docgen-review-and-send';
	}
	return dataTestId;
};

export const SignatureRequestFooter = () => {
	const { token } = theme.useToken();
	const [modal, contextHolder] = Modal.useModal();
	const { notification } = App.useApp();
	const signatureRequestType = useRef<SignatureRequestType>(null);
	const [isGeneratingDocument, setIsGeneratingDocument] = useState(false);

	const history = useHistory();
	const {
		document,
		getDocument,
		updateDocument,
		documentTemplate,
		variables,
		templateMetadata,
		signatureMetadata,
		updateSignatureMetadata,
		updateTemplateMetadata,
	} = useAppStore(state => ({
		document: state.document,
		getDocument: state.getDocument,
		updateDocument: state.updateDocument,
		documentTemplate: state.documentTemplate,
		variables: state.variables,
		templateMetadata: state.templateMetadata,
		signatureMetadata: state.signatureMetadata,
		updateSignatureMetadata: state.updateSignatureMetadata,
		updateTemplateMetadata: state.updateTemplateMetadata,
	}));

	const updateDocumentPayload = (path: string, value: any): PatchPayloadItem => ({
		op: 'replace',
		path,
		value,
	});

	const getListOfDocumentUpdatesPayload = () => {
		const documentUpdateConfig = {
			recipients: {
				path: '/signatureSettings/recipients',
				value: document.signatureSettings.recipients,
				condition: () =>
					document.signatureSettings.recipients &&
					signatureMetadata.currentStep === SignatureRequestSteps.SelectRecipients,
			},
			name: { path: '/name', value: document.name, condition: () => document.name },
			isKbaEnabled: {
				path: '/settings/isKbaEnabled',
				value: document.settings.isKbaEnabled,
				condition: () => document.settings.isKbaEnabled !== documentTemplate.isKbaEnabled,
			},
			isPasscodeEnabled: {
				path: '/settings/isPasscodeEnabled',
				value: document.settings.isPasscodeEnabled,
				condition: () =>
					document.settings.isPasscodeEnabled !== documentTemplate.isPasscodeEnabled,
			},
			expiresInDays: {
				path: '/signatureSettings/expiresInDays',
				value: document.signatureSettings.expiresInDays,
				condition: () =>
					document.signatureSettings.expiresInDays !==
					SIGNATURE_REQUEST_EXPIRATION.DEFAULT,
			},
			passcode: {
				path: '/signatureSettings/passcode',
				value: document.signatureSettings.passcode,
				condition: () =>
					document.settings.isPasscodeEnabled && document.signatureSettings.passcode,
			},
			signerMessage: {
				path: '/signatureSettings/signerMessage',
				value: document.signatureSettings.signerMessage,
				condition: () => document.signatureSettings.signerMessage,
			},
			storageLocation: {
				path: '/signatureSettings/storageLocation',
				value:
					templateMetadata?.containerRID || document.signatureSettings.storageLocation,
				condition: () =>
					templateMetadata?.containerRID || document.signatureSettings.storageLocation,
			},
		};

		const listOfDocumentUpdatesPayload = Object.keys(documentUpdateConfig)
			.map(key => {
				const setting = documentUpdateConfig[key];
				if (setting.condition()) {
					return updateDocumentPayload(setting.path, setting.value);
				}
			})
			.filter(item => item);

		return listOfDocumentUpdatesPayload;
	};

	const getRedirectPath = () => {
		if (templateMetadata?.source === 'esign') {
			return templateMetadata?.redirectPath || signatureRequestDashboardPath;
		}

		return templateDetailsPath
			.replace(':templateRID', encodeURIComponent(documentTemplate.rid))
			.substring(1);
	};

	const pushToSignatureRequestCreatePath = () => {
		sendDocumentPendoTrackingEvent(
			SignatureRequestCreatedFromDocumentTemplateTrackEvent,
			document,
			signatureRequestType.current
		);
		setTimeout(() => {
			history.push({
				pathname: signatureRequestCreatePath,
				state: {
					data: {
						rid: document.rid,
						redirectPath: getRedirectPath(),
						requestType: signatureRequestType.current,
						containerRID: templateMetadata?.containerRID,
					},
				},
			});
		}, 500);
		updateTemplateMetadata({ drawerCloseType: 'complete' });
	};

	const validateRecipients = recipients => {
		if (
			!recipients ||
			recipients.some(recipients => !recipients.name || !recipients.email)
		) {
			return t('invalidRecipientErrorMessage');
		}

		const invalidRecipient = recipients.find(
			recipient => !emailValidator(recipient.email).isValid
		);

		if (invalidRecipient) {
			return emailValidator(invalidRecipient.email).errorMessage;
		}

		return;
	};

	const updateDocumentAtSelectRecipientsStep = async document => {
		const payload: PatchPayload = [
			{
				op: 'replace',
				path: '/signatureSettings/recipients',
				value: document.signatureSettings.recipients,
			},
		];

		try {
			const response = await DocgenClient.updateDocument(payload, document.rid);
			updateDocument(response);
			return response;
		} catch {
			handleErrorWithServerMessage(t('errorMessages.updateDocumentError'));
		}
	};

	const createSignatureRequest = async () => {
		const listOfDocumentUpdatesPayload = getListOfDocumentUpdatesPayload();

		if (listOfDocumentUpdatesPayload.length === 0) {
			pushToSignatureRequestCreatePath();
			return;
		}

		try {
			await DocgenClient.updateDocument(listOfDocumentUpdatesPayload, document.rid);
			pushToSignatureRequestCreatePath();
		} catch {
			handleErrorWithServerMessage(t('errorMessages.updateDocumentError'));
		}
	};

	const VariablesWithNoValuesContent = () => {
		const variablesWithoutValues = variables
			.filter(variable => !variable.value)
			.map(variable => variable.name);
		return (
			<Flex
				vertical
				gap="middle"
				style={{ padding: `${token.paddingXS}px 0 ${token.paddingSM}px` }}
			>
				<Flex vertical gap="small">
					<Text style={{ marginBottom: 0 }}>
						{t('SendSignatureRequest.noVariableComponentInfoModal.subtitle')}
					</Text>
					<Text strong>{variablesWithoutValues.join(', ')}</Text>
				</Flex>
				<div>{t('SendSignatureRequest.noVariableComponentInfoModal.description')}</div>
			</Flex>
		);
	};

	const handleWarningForVariablesWithNoValues = async () => {
		return await modal.confirm({
			title: t('SendSignatureRequest.noVariableComponentInfoModal.title'),
			icon: <ExclamationCircleFilled />,
			content: VariablesWithNoValuesContent(),
			okText: t('SendSignatureRequest.noVariableComponentInfoModal.confirmButtonText'),
			cancelText: t('SendSignatureRequest.noVariableComponentInfoModal.cancelButtonText'),
		});
	};

	const handleConfirmationModalForGenerateDocument = async () => {
		return await modal.confirm({
			title: t('SendSignatureRequest.addFieldDataPanel.generateDocumentModal.title'),
			icon: <ExclamationCircleFilled />,
			content: t(
				'SendSignatureRequest.addFieldDataPanel.generateDocumentModal.description'
			),
			okText: t(
				'SendSignatureRequest.addFieldDataPanel.generateDocumentModal.confirmButtonText'
			),
			cancelText: t(
				'SendSignatureRequest.addFieldDataPanel.generateDocumentModal.cancelButtonText'
			),
		});
	};
	const handleStartOverButtonClick = () => {
		updateTemplateMetadata({ drawerCloseType: 'cancel' });
	};

	const generateDocumentAtPopulateFieldsStep = async document => {
		const payload = {
			mergeAction: DocumentMergeAction.GenerateSignerFields,
		};
		try {
			await DocgenClient.mergeDocument(document.rid, payload);
			try {
				await getDocument(document.rid);
				updateSignatureMetadata({ currentStep: SignatureRequestSteps.SelectRecipients });
			} catch {
				handleError(t('errorMessages.getDocumentError'));
				return;
			}
		} catch {
			handleError(t('errorMessages.generateDocumentError'));
		} finally {
			setIsGeneratingDocument(false);
		}
	};

	const onNextButtonClick = async () => {
		if (signatureMetadata.currentStep === SignatureRequestSteps.PopulateFields) {
			const hasEmptyVariables = variables?.some(variable => !variable.value);
			if (hasEmptyVariables) {
				const isConfirmed = await handleWarningForVariablesWithNoValues();
				if (!isConfirmed) {
					return;
				}
			}
			const isConfirmed = await handleConfirmationModalForGenerateDocument();
			if (!isConfirmed) {
				return;
			}
			setIsGeneratingDocument(true);
			generateDocumentAtPopulateFieldsStep(document);
		} else if (signatureMetadata.currentStep === SignatureRequestSteps.SelectRecipients) {
			const recipients = document?.signatureSettings?.recipients;
			const recipientErrorMessage = validateRecipients(recipients);
			if (recipientErrorMessage) {
				handleError(recipientErrorMessage);
				return;
			}

			const response = await updateDocumentAtSelectRecipientsStep(document);
			if (response) {
				updateSignatureMetadata({
					currentStep: SignatureRequestSteps.ReviewAndSend,
				});
			}
		} else if (signatureMetadata.currentStep === SignatureRequestSteps.ReviewAndSend) {
			if (!document.name) {
				notification.error({
					message: t('SendSignatureRequest.documentNameError'),
				});
			} else if (
				document.settings.isPasscodeEnabled &&
				!document.signatureSettings.passcode
			) {
				notification.error({
					message: t('SendSignatureRequest.securityOptions.passcodeBlankError'),
				});
			} else if (!document.signatureSettings.expiresInDays) {
				notification.error({
					message: t('SendSignatureRequest.expirationDaysNotificationMessage'),
				});
			} else {
				signatureRequestType.current = SignatureRequestType.Quick;
				createSignatureRequest();
			}
		}
	};

	const onPreviousButtonClick = () => {
		if (signatureMetadata.currentStep === SignatureRequestSteps.ReviewAndSend) {
			updateSignatureMetadata({
				currentStep: SignatureRequestSteps.SelectRecipients,
			});
		} else if (signatureMetadata.currentStep === SignatureRequestSteps.SelectRecipients) {
			updateSignatureMetadata({
				currentStep: SignatureRequestSteps.PopulateFields,
			});
		}
	};

	const handleCustomize = () => {
		signatureRequestType.current = SignatureRequestType.Customized;
		createSignatureRequest();
	};
	const shouldShowCustomiseButton = () => {
		return signatureMetadata.currentStep === SignatureRequestSteps.SelectRecipients;
	};

	if (!signatureMetadata) {
		return null;
	}

	return (
		<Flex
			justify="space-between"
			align="center"
			style={{
				background: colorPalette.neutral1,
				flexDirection: 'row-reverse',
				visibility: document ? 'visible' : 'hidden',
				width: '100%',
			}}
		>
			{contextHolder}

			<div>
				{shouldShowCustomiseButton() && (
					<Button
						onClick={handleCustomize}
						data-analytics-id={SendSignatureRequestFooterAnalyticsIds.Customize}
					>
						{t('SendSignatureRequest.footer.customize')}
					</Button>
				)}

				<Tooltip
					placement="right"
					title={
						signatureMetadata.isEditInProgress
							? t('SendSignatureRequest.addFieldDataPanel.signersFieldEdited')
							: ''
					}
					trigger={['hover', 'focus']}
				>
					<Button
						type="primary"
						onClick={onNextButtonClick}
						data-analytics-id={getAnalyticsId(signatureMetadata.currentStep)}
						disabled={signatureMetadata.isEditInProgress || isGeneratingDocument}
						loading={isGeneratingDocument}
						data-testid={getDataTestIdForNextButton(signatureMetadata.currentStep)}
					>
						{getButtonText(signatureMetadata.currentStep, t)}
					</Button>
				</Tooltip>
			</div>

			{templateMetadata?.source !== 'esign' &&
				signatureMetadata.currentStep === SignatureRequestSteps.SelectRecipients && (
					<Button
						onClick={handleStartOverButtonClick}
						data-analytics-id={SendSignatureRequestFooterAnalyticsIds.StartOver}
						data-testid="start-over-button"
					>
						{t('SendSignatureRequest.footer.startOver')}
					</Button>
				)}
			{signatureMetadata.currentStep === SignatureRequestSteps.ReviewAndSend && (
				<Button
					onClick={onPreviousButtonClick}
					data-analytics-id={
						SendSignatureRequestFooterAnalyticsIds.PreviousStepSelectRecipients
					}
				>
					{t('SendSignatureRequest.footer.back')}
				</Button>
			)}

			{templateMetadata?.source === 'esign' &&
				(hasSystemFilledFields(document?.fields)
					? signatureMetadata.currentStep === SignatureRequestSteps.PopulateFields
					: signatureMetadata.currentStep === SignatureRequestSteps.SelectRecipients) && (
					<Button
						onClick={() => updateTemplateMetadata({ drawerCloseType: 'cancel' })}
						data-analytics-id={SendSignatureRequestFooterAnalyticsIds.ChangeTemplate}
					>
						{t('SendSignatureRequest.footer.changeTemplate')}
					</Button>
				)}
		</Flex>
	);
};
